import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import braintree from 'braintree-web';
import classes from 'classnames';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import Summary from './summary';
import ReactGA from 'react-ga';
import DropIn from '../../components/DropIn';
import { FlexDiv, Heading1 } from '@awarego-ui/components';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';

var modal = document.getElementById('modal');
var bankFrame = document.getElementById('bt-modal-body');
var closeFrame = document.getElementById('text-close');

function addFrame(err, iframe) {
    bankFrame.append(iframe);
    modal.removeClass('hidden');
}

function removeFrame() {
    var iframe = bankFrame.find('iframe');
    modal.addClass('hidden');
    iframe.remove();
}

function PaymentForm({ companyId, prevStep, onComplete }) {
    const [clientToken, setClientToken] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [instance, setInstance] = useState(null);
    const instanceRef = useRef(null);
    const mountedRef = useRef(true);

    const { subscriptionStore } = useStores();

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            const currentInstance = instanceRef.current;
            if (currentInstance?.threeDSecure) {
                currentInstance.threeDSecure.teardown().catch(() => {});
            }
            instanceRef.current = null;
        };
    }, []);

    useEffect(() => {
        (async () => {
            const clientToken = await subscriptionStore.loadToken(companyId);
            if (mountedRef.current) {
                setClientToken(clientToken);
            }
        })();
    }, [companyId]);

    const buy = async () => {
        const currentInstance = instanceRef.current;
        if (!currentInstance) {
            console.error('Payment instance not ready yet');
            return;
        }

        const price = parseFloat(subscriptionStore.price) || 0;
        const isFreePlan = price === 0;

        // For free plans, 3D Secure is not required
        if (!isFreePlan && !currentInstance.threeDSecure) {
            console.error('3D Secure not initialized');
            return;
        }

        ReactGA.event({
            category: 'User',
            action: 'Purchase',
            label: 'Button clicked',
        });

        // Send the nonce to your server
        let payload;
        setWaiting(true);
        try {
            payload = await currentInstance.requestPaymentMethod();
        } catch (e) {
            if (!mountedRef.current) return;
            setWaiting(false);
            console.error(e);
            ReactGA.event({
                category: 'User',
                action: 'Purchase',
                label: 'Failed - requestPaymentMethod error',
            });
            return;
        }

        if (!payload || !payload.nonce) {
            if (!mountedRef.current) return;
            setWaiting(false);
            console.error('Failed to get payment method');
            ReactGA.event({
                category: 'User',
                action: 'Purchase',
                label: 'Failed - no payment method',
            });
            return;
        }

        // For free plans, skip 3D Secure and use the nonce directly
        if (isFreePlan) {
            ReactGA.event({
                category: 'User',
                action: 'Purchase',
                label: 'Success',
            });
            if (!mountedRef.current) return;
            setWaiting(false);
            onComplete(payload.nonce);
            return;
        }

        // For paid plans, verify with 3D Secure
        try {
            payload = await currentInstance.threeDSecure.verifyCard({
                amount: subscriptionStore.price,
                nonce: payload.nonce,
                addFrame: addFrame,
                bin: payload.details.bin,
                removeFrame: removeFrame,
                onLookupComplete: (data, next) => {
                    if (mountedRef.current) {
                        setWaiting(false);
                    }
                    next();
                },
            });

            if (!mountedRef.current) return;

            ReactGA.event({
                category: 'User',
                action: 'Purchase',
                label: 'Success',
            });

            onComplete(payload.nonce);
        } catch (e) {
            if (!mountedRef.current) return;
            setWaiting(false);
            console.error(e);
            ReactGA.event({
                category: 'User',
                action: 'Purchase',
                label: 'Failed',
            });
        }
    };

    const instanceCreated = async (instanceArg) => {
        if (!mountedRef.current) return;
        try {
            instanceArg.threeDSecure = await braintree.threeDSecure.create({
                version: 2, // Will use 3DS 2 whenever possible
                client: instanceArg._client,
            });
            if (!mountedRef.current) {
                await instanceArg.threeDSecure.teardown().catch(() => {});
                return;
            }
            closeFrame.click(function () {
                instanceArg.threeDSecure.cancelVerifyCard(removeFrame());
                //enablePayNow();
            });
            instanceRef.current = instanceArg;
            setInstance(instanceArg);
        } catch (e) {
            console.error(e);
        }
    };

    const {
        price,
        futureCredit,
        nextBilling,
        my,
        planPeriod,
        payInProcess,
        selectedTier,
        error,
    } = subscriptionStore;

    if (!clientToken) {
        return (
            <div className={'wizard-fullscreen lightbox-like'}>
                <div className={'wizard-fullscreen-header'}>
                    <Heading1>Payment Details</Heading1>
                </div>
                <div className={'wizard-fullscreen-body'}>
                    <FlexDiv
                        fullHeight
                        fullWidth
                        alignCenter
                        justifyCenter
                        gap={16}
                        column
                    >
                        <Heading1 themed>One moment...</Heading1>
                        <ThemedSpinner />
                    </FlexDiv>
                </div>
                <div className={'wizard-fullscreen-footer'}>
                    <div className={'actions'}></div>
                </div>
            </div>
        );
    } else {
        return (
            <div className={'wizard-fullscreen lightbox-like'}>
                <div className={'wizard-fullscreen-header'}>
                    <Heading1>Payment Details</Heading1>
                </div>
                <div
                    className={classes(
                        'wizard-fullscreen-body align-items-center justify-content-center flex-column',
                        { loading: waiting || payInProcess }
                    )}
                >
                    <div className="paymentError">
                        <ErrorMessage error={error} />
                    </div>

                    <DropIn
                        options={{
                            authorization: clientToken,
                            card: { cardholderName: true },
                        }}
                        onInstance={instanceCreated}
                    />

                    {(waiting || payInProcess) && (
                        <div className="wait-overlay d-flex align-items-center justify-content-center flex-fill flex-column">
                            <Heading1 themed>One moment...</Heading1>
                            <ThemedSpinner />
                        </div>
                    )}
                </div>

                <div className={'wizard-fullscreen-footer'}>
                    <div className={'actions center '}>
                        <Button color={'link'} size={'lg'} onClick={prevStep}>
                            Previous
                        </Button>

                        <div className="d-flex flex-row align-items-center price">
                            <strong>Summary:</strong>
                            <div className="d-flex flex-column">
                                <Summary
                                    {...{
                                        price,
                                        futureCredit,
                                        nextBilling,
                                        my,
                                        planPeriod,
                                    }}
                                />

                                {selectedTier && (
                                    <div className="price-description">
                                        {selectedTier.starting_unit}-
                                        {selectedTier.ending_unit || 500}{' '}
                                        employees
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button
                            color={'primary'}
                            onClick={buy}
                            disabled={
                                !instance ||
                                (parseFloat(subscriptionStore.price) > 0 &&
                                    !instance.threeDSecure) ||
                                waiting ||
                                payInProcess
                            }
                        >
                            Purchase
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default observer(PaymentForm);
